"use client";
import parse, { domToReact } from "html-react-parser";
import { format, parseISO } from "date-fns";
import DOMPurify from 'isomorphic-dompurify';
import classes from "./ArticleContent.module.scss";
import ShareContainer from "./ShareContainer/ShareContainer";
import { usePathname, useRouter } from "next/navigation";
import Sidebar from "../Sidebar/Sidebar";
import Image from "next/image";
import { formatDistanceToNow } from "date-fns";
import { addBookMarkArticleByUser } from "@/actions/fetchQueryData";
import React, { useState } from "react";
import { message } from "antd";
import BookMark from "@/assets/icons/BookMark";
import "@ant-design/v5-patch-for-react-19";
import { useSession } from "@/lib/hooks";

interface articleProps {
  artickeBookmarked?: boolean;
  articleData: any;
  type?:'preview'
}

const addUniqueIdToH2 = (htmlString: string) => {
  let idCounter = 1;
  const h2Contents: string[] = [];

  const fixLinks = (htmlString: string) => {
    return htmlString.replace(/href="(www\.[^"]+)"/g, 'href="https://$1"');
  };  

  const cleanHtmlString =  DOMPurify.sanitize(fixLinks(htmlString), {
    ALLOWED_ATTR: ["id", "class", "href"], 
  });

  const updatedHtml = parse(cleanHtmlString, {
    replace: (node: any) => {
      if (node.name === "h2") {
        const textContent = domToReact(node.children) as string;
        h2Contents.push(textContent);
        node.attribs.id = `heading-${idCounter++}`;
        return node;
      }
    },
  });

  return { updatedHtml, h2Contents };
};

const Article = ({ artickeBookmarked = false, articleData ,type}: articleProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const { session } = useSession();

  const [bookmarkArticle, setBookmarkArticle] = useState(artickeBookmarked);

  const { h2Contents, updatedHtml } = addUniqueIdToH2(articleData?.content);
  const fullArticleUrl =
    typeof window !== "undefined" ? window.location.origin + pathname : "";

  const isoString = articleData?.createdAt;
  const localDate = parseISO(isoString);
  const formattedDate = format(localDate, "yyyy-MM-dd");

  const onBookMarkClick = async () => {
    if (!session?.isAuthenticated) {
      message.error("Please login to bookmark article", 1.5);

      setTimeout(() => {
        router.push("/login");
      }, 500);
      return;
    }

    try {
      const bookmarkData = await addBookMarkArticleByUser(
        articleData._id,
        !bookmarkArticle
      );

      if (bookmarkData && !bookmarkArticle) {
        setBookmarkArticle(true);
        message.success("Article bookmarked successfully");
      } else {
        message.success("Article removed from bookmark");
        setBookmarkArticle(false);
      }
    } catch {
      message.error("Unable to do action");
    }
  };

  return (
    <div className={classes["article-container"]}>
      <Sidebar
        sideBarArr={h2Contents.map((content, index) => ({
          header: content,
          id: `heading-${index + 1}`,
        }))}
      />

      <div className={classes["article-content-container"]}>
        <div className={classes["header"]}>
          <span className={classes["title"]}>
            {articleData?.category?.[0]?.name}
          </span>
        </div>

        <h1 className={classes["article-title"]}>{articleData?.title}</h1>

        <div className={classes["authours-share-container"]}>
          <div className={classes["content"]}>
            <div className={classes["authors"]}>
              <span>By</span>
              <span
                className={classes["highlight"]}
                onClick={() => router.push(`/author/${articleData?.authorId}`)}
              >
                {`${
                  articleData?.author?.firstName +
                    " " +
                    articleData?.author?.lastName || ""
                }`}
              </span>
            </div>

            <div className={classes["date-time"]}>
              {formattedDate}
              <span className={classes["dot"]}>.</span>
              <span className={classes["read-time"]}>
                {articleData?.createdAt
                  ? formatDistanceToNow(articleData?.createdAt) + " ago"
                  : ""}
              </span>
            </div>
          </div>

          <div className={classes["bookmark-share-container"]}>
            <BookMark isFilled={bookmarkArticle} onClick={onBookMarkClick} />

            <ShareContainer
              articleUrl={fullArticleUrl}
              content={articleData?.title}
            />
          </div>
        </div>
<div className={classes['article-img-container-wrapper']}>
        <div className={classes["article-img-container"]}>
          <Image
            src={
              type === 'preview' 
                ? articleData.imageUrl
                : `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL}/${articleData?.imageUrl}`
            }
            alt={articleData?.title}
            fill
            className={classes["article-img"]}
          />
        </div>
        <div className={classes["image-caption"]}>{articleData?.imageCaption || articleData?.title}</div>
        </div>

        <div className={classes["article-content"]}>
          {updatedHtml}

          {articleData?.tags?.length > 0 && (
            <React.Fragment>
              <h2>Related Topics</h2>

              <div className={classes["tags-container"]}>
                {articleData?.tags?.map((tag: any) => (
                  <span key={tag?._id} className={classes["tag"]}>
                    {tag?.name}
                  </span>
                ))}
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default Article;
